import { useState } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/components/app/common";
import { DataTable } from "@/components/app/DataTable";
import { useMe } from "@/components/app/MeContext";
import { NewInvoiceDialog } from "@/components/invoice/NewInvoiceDialog";
import { InvoiceAssetDialog } from "@/components/invoice/InvoiceAssetDialog";
import { InvoiceSheet } from "@/components/invoice/InvoiceSheet";
import { INV_COLS } from "@/components/invoice/invoiceCols";
import { useFetch } from "@/hooks/useFetch";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { CATEGORIES } from "@/components/reports/reportDefs";
import { ReportView } from "@/pages/Reports";
import { DispatchSheet } from "@/components/logistics/DispatchSheet";

export default function Invoices() {
  const [tab, setTab] = useState("invoices");
  return (
    <div data-testid="invoices-page">
      <div className="flex gap-1 mb-4 border-b border-slate-200" data-testid="register-tabs">
        {[["invoices", "Invoice register"], ["challans", "Delivery challan register"]].map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`register-tab-${k}`}
            className={`px-3 py-2 text-sm -mb-px border-b-2 transition-colors ${tab === k ? "border-sky-700 text-sky-800 font-semibold" : "border-transparent text-slate-500 hover:text-slate-800"}`}>{l}</button>))}
      </div>
      {tab === "invoices" ? <InvoiceRegister /> : <ChallanRegister />}
    </div>
  );
}

const DC_DEF = CATEGORIES.flatMap((c) => c.reports).find((r) => r.key === "dc-register");

function ChallanRegister() {
  const [dsp, setDsp] = useState(null);
  const open = async (r) => {
    try { const res = await api.get(`/dispatches?q=${encodeURIComponent(r.dispatch_no)}`); setDsp(res.data.find((d) => d.id === r.id) || null); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div data-testid="challan-register">
      <PageHeader title="Delivery Challan Register" subtitle="Every LM / LMO / LMS delivery challan, auto-numbered per financial year. Open a row to download the 3 copies or email the challan." />
      <ReportView def={DC_DEF} onRowClick={open} />
      <DispatchSheet dispatch={dsp} onClose={() => setDsp(null)} onDone={() => setDsp(null)} />
    </div>
  );
}

function InvoiceRegister() {
  const { can } = useMe();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("active");
  const { data, reload } = useFetch(`/invoices?q=${encodeURIComponent(q)}&status=${status === "all" ? "" : status}`);
  const [open, setOpen] = useState(false);
  const [focus, setFocus] = useState(null);
  const [assetInv, setAssetInv] = useState(null);
  return (
    <div data-testid="invoice-register">
      <PageHeader title="Invoice Register" subtitle="Every invoice raised against an LM, with its lines, delivery address and the shipments that carried it. Values are basic (without GST) unless marked.">
        {can("write") && <Button onClick={() => setOpen(true)} data-testid="invoice-new-btn"><Plus className="h-4 w-4 mr-1" />New invoice</Button>}
      </PageHeader>
      <div className="flex flex-wrap gap-2 mb-3">
        <Input className="max-w-sm bg-white" placeholder="Search invoice, LM, customer, PO, city…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="invoice-search" />
        <Select value={status} onValueChange={setStatus}><SelectTrigger className="w-40 bg-white" data-testid="invoice-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="cancelled">Cancelled</SelectItem><SelectItem value="all">All</SelectItem></SelectContent></Select>
      </div>
      {can("assets.edit") && (data || []).some((i) => i.asset_prompt === "pending" && i.status === "active") && (
        <div className="mb-3 rounded border border-indigo-200 bg-indigo-50/60 px-3 py-2 text-xs flex flex-wrap items-center gap-2" data-testid="invoices-asset-pending">
          <b className="text-indigo-900">LMR invoices to add as assets:</b>
          {data.filter((i) => i.asset_prompt === "pending" && i.status === "active").map((i) => <Button key={i.id} size="sm" variant="outline" className="h-7" onClick={() => setAssetInv(i.id)} data-testid={`invoices-asset-open-${i.invoice_no}`}>{i.invoice_no}</Button>)}
        </div>
      )}
      <DataTable testid="invoices-table" columns={INV_COLS} rows={data} onRowClick={(r) => setFocus(r.id)} exportName="Invoice_Register" exportPerm="dispatch.export" empty="No invoices yet" />
      {open && <NewInvoiceDialog onClose={() => setOpen(false)} onSaved={(inv) => { reload(); if (inv?.asset_prompt === "pending" && can("assets.edit")) setAssetInv(inv.id); }} />}
      {assetInv && <InvoiceAssetDialog invoiceId={assetInv} onClose={() => setAssetInv(null)} onDone={reload} />}
      <InvoiceSheet key={focus} id={focus} onClose={() => setFocus(null)} onDone={() => { setFocus(null); reload(); }} />
    </div>
  );
}
